/**
 * A multi-city itinerary's route as ONE PAIR PER LEG — "TLV → TBS, CDG → TLV".
 *
 * THE BUG THIS EXISTS TO KILL
 * ---------------------------
 * Every surface that had to print a multi-city route built the same chain by
 * hand: leg 1's departure, then each leg's arrival, joined with an arrow.
 *
 *   [legs[0][0].from, ...legs.map(l => l[l.length - 1].to)].join(' → ')
 *
 * That is only correct for an itinerary where every leg departs from where the
 * previous one landed. Multi-city legs are chosen independently, so an OPEN JAW
 * is a normal, bookable trip — fly TLV→TBS, make your own way to Paris, fly
 * CDG→TLV. The chain renders that as `TLV → TBS → TLV`: it asserts a continuous
 * trip, and it deletes CDG from the string entirely. The customer's last screen
 * before paying claimed they fly home from Tbilisi.
 *
 * The backoffice variant was worse still. It chained every SEGMENT of a
 * flattened `legs.flat()`, so intra-leg layover airports and real leg boundaries
 * came out indistinguishable: `TLV - MXP - VIE - TBS - TLV`. Nothing in that
 * string says which of those the traveller has to arrange transport between.
 *
 * WHY PAIRS ARE NEVER COLLAPSED
 * -----------------------------
 * `TLV → TBS, TBS → IST` keeps the repeated TBS rather than folding to
 * `TLV → TBS → IST`. A multi-city itinerary is N separately chosen flights, and
 * the format says so every time — so a continuous trip and an open jaw render
 * in the SAME shape, and a reader never has to spot punctuation to notice which
 * one they are holding.
 *
 * WHY THIS TAKES A FIELD NAME RATHER THAN RETURNING STRINGS
 * --------------------------------------------------------
 * Callers disagree about which half of a location to print, and all of them are
 * right: the b2c cart prints `name` (matching its own one-way/round-trip branch),
 * and the backoffice order row prints `code` in the table cell and `name` in its
 * long form. Note that for offers from the Amadeus/Cockpit mappers the two are
 * the same text — both emit `from: { name: iataCode, code: iataCode }` — so this
 * only diverges for records whose locations were enriched elsewhere.
 */

export interface RouteLocation {
  code?: string | null;
  name?: string | null;
}

/** The only fields this module reads from a mapped flight segment. */
export interface MultiCityRouteSegment {
  from?: RouteLocation | null;
  to?: RouteLocation | null;
}

export interface MultiCityLegEndpoints {
  /** Index into the ORIGINAL legs array, so callers can cross-reference. */
  legIndex: number;
  from: RouteLocation | null;
  to: RouteLocation | null;
}

export interface FormatLegRouteOptions {
  /** Between a leg's two endpoints. */
  within?: string;
  /** Between one leg and the next. */
  between?: string;
}

const DEFAULTS: Required<FormatLegRouteOptions> = {
  within: ' → ',
  between: ', ',
};

const text = (
  location: RouteLocation | null | undefined,
  field: 'code' | 'name'
): string => {
  const value = location?.[field];
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * Each leg's own endpoints: its FIRST segment's departure and its LAST segment's
 * arrival — so a leg with a connection reports where it actually lands rather
 * than its layover airport.
 *
 * Never throws. A null or empty leg is skipped rather than treated as a break in
 * the route, matching `findAirportChanges` and `findLayovers` — the three run
 * over the same `legs` on the same surfaces, and a leg invisible to one must be
 * invisible to all of them.
 */
export function legEndpoints(
  legs?: (MultiCityRouteSegment[] | null | undefined)[] | null
): MultiCityLegEndpoints[] {
  return (Array.isArray(legs) ? legs : [])
    .map((leg, legIndex) => {
      const segments = (Array.isArray(leg) ? leg : []).filter(
        Boolean
      ) as MultiCityRouteSegment[];
      if (!segments.length) return null;
      return {
        legIndex,
        from: segments[0]?.from ?? null,
        to: segments[segments.length - 1]?.to ?? null,
      };
    })
    .filter(Boolean) as MultiCityLegEndpoints[];
}

/**
 * "TLV → TBS, CDG → TLV".
 *
 * A leg missing EITHER endpoint is dropped rather than printed half-formed: a
 * lone "TLV" in a list of pairs reads as a place the trip visits rather than as
 * missing data. Returns '' when nothing is printable, which every caller can
 * render as "no route" without a special case.
 */
export function formatLegRoute(
  legs?: (MultiCityRouteSegment[] | null | undefined)[] | null,
  field: 'code' | 'name' = 'code',
  options?: FormatLegRouteOptions
): string {
  const { within, between } = { ...DEFAULTS, ...options };

  return legEndpoints(legs)
    .map(({ from, to }) => {
      const departure = text(from, field);
      const destination = text(to, field);
      if (!departure || !destination) return '';
      return `${departure}${within}${destination}`;
    })
    .filter(Boolean)
    .join(between);
}
